import { UserIcon } from "@heroicons/react/24/outline";
import { EVERYONE_SENTINEL, UserId } from "@sp-crm/core";
import {
    StatelessSearchbarItem,
    StatelessSearchbarItemContext,
} from "components/community-search/search-bar/stateless-searchbar-item";
import { SelectableUser } from "components/shared/user-select";
import { Radio } from "components/ui/radio";
import { useGetUsersForRegionQuery } from "generated/graphql";
import React, { useContext } from "react";
import { useCurrentUserId, useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";

interface ClientMapEmployeeFilterProps {
    value: SelectableUser;
    onChange: (userId: SelectableUser) => void;
}

const unassignedValue: SelectableUser = "";

interface EmployeeOption {
    value: SelectableUser;
    label: string;
}

export const ClientMapEmployeeFilter: React.FC<ClientMapEmployeeFilterProps> = props => {
    const { value, onChange } = props;
    const regionId = useRegionId();
    const currentUserId = useCurrentUserId();
    const closeCallout = useContext(StatelessSearchbarItemContext);
    const users = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const sortedUsers = (users.data?.getUsersForRegion ?? [])
        .filter(u => !u.hidden || u.id === value)
        .slice()
        .sort((a, b) =>
            a.preferredName
                .toLocaleLowerCase()
                .localeCompare(b.preferredName.toLocaleLowerCase()),
        );

    const everyoneOption: EmployeeOption = {
        value: EVERYONE_SENTINEL,
        label: "Everyone",
    };
    const unassignedOption: EmployeeOption = {
        value: unassignedValue,
        label: "Unassigned",
    };
    const userOptions: EmployeeOption[] = sortedUsers.map(u => ({
        value: u.id as UserId,
        label: u.id === currentUserId ? `${u.preferredName} (me)` : u.preferredName,
    }));
    const options: EmployeeOption[] = [everyoneOption, unassignedOption, ...userOptions];

    const selectedOption = options.find(option => option.value === (value ?? ""));
    const triggerLabel =
        selectedOption && selectedOption.value !== EVERYONE_SENTINEL
            ? `Employee: ${selectedOption.label}`
            : "Employee";

    const chooseOption = (option: EmployeeOption) => {
        onChange(option.value);
        closeCallout();
    };

    return (
        <StatelessSearchbarItem
            content={
                <div className="flex items-center space-x-2">
                    <UserIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{triggerLabel}</span>
                </div>
            }>
            <div className="w-64 p-1 space-y-2">
                <Radio
                    name="client-map-employee"
                    checked={everyoneOption.value === (value ?? "")}
                    onChange={() => chooseOption(everyoneOption)}
                    label={everyoneOption.label}
                />
                <Radio
                    name="client-map-employee"
                    checked={unassignedOption.value === (value ?? "")}
                    onChange={() => chooseOption(unassignedOption)}
                    label={unassignedOption.label}
                />
                <div className="border-t border-gray-200 pt-2 space-y-2 max-h-64 overflow-y-auto">
                    {userOptions.map(option => (
                        <Radio
                            key={option.value}
                            name="client-map-employee"
                            checked={option.value === (value ?? "")}
                            onChange={() => chooseOption(option)}
                            label={option.label}
                        />
                    ))}
                </div>
            </div>
        </StatelessSearchbarItem>
    );
};
